"use client"

import { Avatar } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import type { Brief } from "@/types"

interface CollabHeaderProps {
  brief: Brief
}

const MAX_AVATARS = 5

export function CollabHeader({ brief }: CollabHeaderProps) {
  // Use the resource-supplied partition + counts directly. Avoid
  // re-deriving filledSlots / total here. `brief.progress` is the
  // single source of truth (carve-up critique #1).
  const allContributors = [
    ...brief.featured_contributors,
    ...brief.track_contributors,
  ]
  const visibleContributors = allContributors.slice(0, MAX_AVATARS)
  const overflowCount = allContributors.length - MAX_AVATARS

  return (
    <div className="flex flex-col gap-4">
      <p className="text-tertiary text-xs font-medium tracking-wider uppercase">
        Community Playlist Brief
      </p>

      <h1 className="text-display-sm text-primary lg:text-display-md font-semibold">
        {brief.title}
      </h1>

      {brief.subtitle && (
        <p className="text-secondary text-lg">{brief.subtitle}</p>
      )}

      <div className="text-tertiary flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        {allContributors.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex items-center -space-x-2">
              {visibleContributors.map((contributor) => (
                <div
                  key={contributor.id}
                  className="rounded-full ring-2 ring-white"
                >
                  <Avatar
                    name={contributor.display_name}
                    avatarUrl={contributor.user?.avatar_url}
                    size="xs"
                  />
                </div>
              ))}
              {overflowCount > 0 && (
                <div className="bg-secondary_subtle text-secondary flex size-6 items-center justify-center rounded-full text-[10px] font-medium ring-2 ring-white">
                  +{overflowCount}
                </div>
              )}
            </div>
            <span>
              {brief.contributors_count}{" "}
              {brief.contributors_count === 1 ? "person" : "people"}
              <span aria-hidden className="px-1.5">
                ·
              </span>
              {brief.suggestions_count}{" "}
              {brief.suggestions_count === 1 ? "answer" : "answers"}
            </span>
          </div>
        )}

        <SlotTray progress={brief.progress} />
      </div>
    </div>
  )
}

// A score track, not a completion meter: one ink diamond per slot
// (borrowed from the end-mark motif), filled as tracks are chosen. The
// single strongest de-LMS move in the header — nothing here says
// "percent complete".
function SlotTray({ progress }: { progress: Brief["progress"] }) {
  const { filled_slots_count, total_slots } = progress
  if (total_slots === 0) return null

  return (
    <div
      role="img"
      aria-label={`${filled_slots_count} of ${total_slots} tracks chosen`}
      className="flex items-center gap-1.5"
    >
      {Array.from({ length: total_slots }).map((_, i) => (
        <span
          key={i}
          aria-hidden
          className={cn(
            "size-2 rotate-45",
            i < filled_slots_count ? "bg-batyr-ink" : "bg-batyr-ink/20",
          )}
        />
      ))}
    </div>
  )
}
